'use client';

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { notFound } from 'next/navigation';
import Link from 'next/link';
import Select from 'react-select';
import ReCAPTCHA from 'react-google-recaptcha';
import toast from 'react-hot-toast';

import BlogOurClients from './BlogOurClients';
import { BlogLInkIcon as Icon_BlogLInkIcon } from "@/components/customer/icons/BlogLInkIcon";
import { FooterFaceBook as Icon_FooterFaceBook } from "@/components/customer/icons/FooterFaceBook";
import { FooterInsta as Icon_FooterInsta } from "@/components/customer/icons/FooterInsta";
import { FooterLinkedIn as Icon_FooterLinkedIn } from "@/components/customer/icons/FooterLinkedIn";
import { FooterWhatsapp as Icon_FooterWhatsapp } from "@/components/customer/icons/FooterWhatsapp";
const SvgIcons = { BlogLInkIcon: Icon_BlogLInkIcon, FooterFaceBook: Icon_FooterFaceBook, FooterInsta: Icon_FooterInsta, FooterLinkedIn: Icon_FooterLinkedIn, FooterWhatsapp: Icon_FooterWhatsapp };
import CommentSectionView from '../customer/CommentSectionView';
import { PROJECTS_DELIVERED, HAPPY_CUSTOMERS } from '@/lib/homepageStats';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import { getImageUrl, getFlagUrl } from '@/lib/imageUrl';
import { getApiUrl } from "@/lib/apiUrl";
import { stripDangerousChars } from "@/lib/validation";
import { sanitizeHtml } from "@/lib/sanitize";

// ===== Helper: Decode HTML entities from API =====
const decodeHTMLEntities = (html: string): string => {
  if (typeof window === 'undefined') {
    return html
      .replace(/\\u003C/g, '<')
      .replace(/\\u003E/g, '>')
      .replace(/\\u0026/g, '&')
      .replace(/\u003C/g, '<')
      .replace(/\u003E/g, '>')
      .replace(/\u0026/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  }
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
};

// ===== Process content - preserves ALL inline styles from CMS =====
const processContent = (rawContent: string | undefined): string => {
  if (!rawContent) return '';
  const decoded = decodeHTMLEntities(rawContent);
  return decoded.replace(/<h1(\s[^>]*)?>/gi, '<h2$1>').replace(/<\/h1>/gi, '</h2>');
};

// ===== Related-posts topical ranking =====
// blogs.json stores `categories` as a JSON-stringified array of category-id strings and `tags`
// as a comma-separated keyword string. We rank other posts by shared category (strong) + shared
// tags (weak) so "Related posts" is a real topical cluster instead of every post in date order.
const RELATED_POSTS_LIMIT = 12;

const parseBlogCategories = (raw: any): string[] => {
  if (Array.isArray(raw)) return raw.map((c) => String(c).trim()).filter(Boolean);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((c) => String(c).trim()).filter(Boolean);
    } catch {
      /* fall through to comma-split */
    }
    return raw.split(',').map((c) => c.trim()).filter(Boolean);
  }
  return [];
};

const parseBlogTags = (raw: any): string[] => {
  if (Array.isArray(raw)) return raw.map((t) => String(t).trim().toLowerCase()).filter(Boolean);
  if (typeof raw === 'string') return raw.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
  return [];
};

const rankRelatedBlogs = (current: any, all: any[], currentSlug: string): any[] => {
  if (!Array.isArray(all)) return [];
  const curCats = new Set(parseBlogCategories(current?.categories));
  const curTags = new Set(parseBlogTags(current?.tags));
  return all
    .filter((b) => b?.slug && b.slug !== currentSlug)
    .map((b) => {
      const sharedCats = parseBlogCategories(b.categories).filter((c) => curCats.has(c)).length;
      const sharedTags = parseBlogTags(b.tags).filter((t) => curTags.has(t)).length;
      return { blog: b, score: sharedCats * 10 + sharedTags };
    })
    .sort((a, z) => {
      if (z.score !== a.score) return z.score - a.score;
      const at = new Date(a.blog?.updated_at || a.blog?.created_at || 0).getTime();
      const zt = new Date(z.blog?.updated_at || z.blog?.created_at || 0).getTime();
      return zt - at;
    })
    .slice(0, RELATED_POSTS_LIMIT)
    .map((x) => x.blog);
};

interface Country {
  id: number;
  country: string;
  short_code: string;
  calling_code: number;
  phone_length: number;
  status: number;
  flag: string;
  currency: string;
  currency_symbol: string;
}

interface CustomOption {
  value: string;
  label: string;
  flag: string;
  calling_code: string;
  country_name: string;
  phone_length: number;
}

interface BlogCategory {
  id: number;
  name: string;
  seo_url: string;
}

interface BlogDetailViewProps {
  blog: any;        // Current blog data
  blogs: any[];     // All blogs for related posts
  slug: string;
}

const BlogDetailView = ({ blog, blogs, slug }: BlogDetailViewProps) => {
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [formLoading, setFormLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [isCountryLoading, setIsCountryLoading] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [isLinkCopied, setIsLinkCopied] = useState(false);

  // ✅ FIX: Move useRef BEFORE any conditional return
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  // "About Vilva" sidebar stats — was hardcoded (10+ / 1000+ / 50+); now
  // pulled from the same real snapshot other pages use, with the same
  // scroll-triggered count-up. No "updated X ago" line here — that's
  // deliberate, unlike StatusSection.tsx.
  const [aboutYears, setAboutYears] = useState(0);
  const [aboutCustomers, setAboutCustomers] = useState(0);
  const [aboutProjects, setAboutProjects] = useState(0);
  const [aboutStatsAnimated, setAboutStatsAnimated] = useState(false);
  const aboutStatsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      setAboutYears(10);
      setAboutCustomers(HAPPY_CUSTOMERS);
      setAboutProjects(PROJECTS_DELIVERED);
      setAboutStatsAnimated(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !aboutStatsAnimated) {
          setAboutStatsAnimated(true);

          const duration = 2000;
          const steps = 50;
          const yearsTarget = 10;
          const customersTarget = HAPPY_CUSTOMERS;
          const projectsTarget = PROJECTS_DELIVERED;
          const intervalTime = duration / steps;
          let currentStep = 0;

          const interval = setInterval(() => {
            currentStep++;
            if (currentStep >= steps) {
              clearInterval(interval);
              setAboutYears(yearsTarget);
              setAboutCustomers(customersTarget);
              setAboutProjects(projectsTarget);
              return;
            }
            setAboutYears((prev) => Math.min(prev + yearsTarget / steps, yearsTarget));
            setAboutCustomers((prev) => Math.min(prev + customersTarget / steps, customersTarget));
            setAboutProjects((prev) => Math.min(prev + projectsTarget / steps, projectsTarget));
          }, intervalTime);
        }
      },
      { threshold: 0 }
    );

    const node = aboutStatsRef.current;
    if (node) observer.observe(node);

    // Backstop: if the observer never fires (e.g. element already in a
    // weird state), still land on the real figures rather than zeros.
    const failsafe = window.setTimeout(() => {
      setAboutStatsAnimated((already) => {
        if (already) return already;
        setAboutYears(10);
        setAboutCustomers(HAPPY_CUSTOMERS);
        setAboutProjects(PROJECTS_DELIVERED);
        return true;
      });
    }, 4000);

    return () => {
      if (node) observer.unobserve(node);
      window.clearTimeout(failsafe);
    };
  }, [aboutStatsAnimated]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleCopyLink = () => {
    if (!isClient) return;
    navigator.clipboard.writeText(window.location.href).then(() => {
      setIsLinkCopied(true);
      setTimeout(() => setIsLinkCopied(false), 5000);
    });
  };

  const assetscountries = getFlagUrl();
  const recaptchaSiteKey = '6Lf9iEYqAAAAAGBkHMfKHyrXoDP3tyX_yZ8cL9b-';

  const [formData, setFormData] = useState({
    category: '',
    name: '',
    email: '',
    country: '',
    countryCode: '',
    mobile: '',
    city: '',
    postcode: '',
    company: '',
    description: '',
    'g-recaptcha-response': '',
  });

  // Lead-form prefill (countries + IP geo) is deferred until the reader engages the form, so a
  // blog read no longer fires 3 network calls on load for a form most readers never touch.
  const [formActivated, setFormActivated] = useState(false);

  // Fetch countries
  useEffect(() => {
    if (!formActivated) return;
    setIsCountryLoading(true);
    fetch("/api/countries")
      .then((response) => response.json())
      .then((data: Country[]) => {
        setCountries(Array.isArray(data) ? data : []);
        setIsCountryLoading(false);
      })
      .catch((error) => console.error('Error fetching countries:', error));
  }, [formActivated]);

  // Auto-detect country from IP, defaulting to India while the lookup resolves
  useEffect(() => {
    if (countries.length > 0) {
      if (!formData.country) {
        const india = countries.find((c) => c.calling_code === 91);
        if (india) {
          setFormData((prev) => ({
            ...prev,
            country: india.id.toString(),
            countryCode: '+91',
          }));
        }
      }

      // Server-side lookup (no CORS/rate-limit exposure in the browser — see app/api/geo).
      fetch('/api/geo')
        .then((res) => {
          if (!res.ok) throw new Error('API unavailable');
          return res.json();
        })
        .then((data) => {
          const matchingCountry = countries.find((c) => c.short_code === data.country_code);
          if (matchingCountry) {
            setFormData((prev) => ({
              ...prev,
              country: matchingCountry.id.toString(),
              countryCode: `+${matchingCountry.calling_code}`,
            }));
          }
        })
        .catch(() => {
          // Default is already set to India, no action needed on failure
        });
    }
  }, [countries]);

  const currentUrl = isClient ? window.location.href : '';
  const shareText = blog
    ? encodeURIComponent(
        `Check out: ${blog.title} - ${blog.short_content?.replace(/<[^>]+>/g, '').slice(0, 100)}...`,
      )
    : '';
  const shareUrl = encodeURIComponent(currentUrl);
  const shareImage = blog?.image
    ? encodeURIComponent(getImageUrl(`/${blog.image}`))
    : '';

  // Topically-ranked cluster of related posts (shared category/tags), not the full flat list.
  const relatedBlogs = rankRelatedBlogs(blog, blogs, slug);

  // Fetch blog categories
  // useEffect(() => {
  //   fetch(
  //     getImageUrl("/vilvabusiness/blog_categories.json"),
  //   )
  //     .then((response) => {
  //       if (!response.ok) throw new Error('Network response was not ok');
  //       return response.json();
  //     })
  //     .then((data: BlogCategory[]) => {
  //       setCategories(data);
  //     })
  //     .catch((error: Error) => {
  //       console.error('Error fetching categories:', error);
  //       setError(error.message);
  //     });
  // }, []);



  // Map countries to custom options for react-select
  const customOptions: CustomOption[] = countries.map((country) => ({
    value: country.id.toString(),
    label: `+${country.calling_code}`,
    flag: country.flag ? `${assetscountries}${country.flag}` : '',
    calling_code: `+${country.calling_code}`,
    country_name: country.country,
    phone_length: country.phone_length,
  }));

  // Format the dropdown option to show flag and country name
  const formatOptionLabel = ({
    calling_code,
    flag,
    country_name,
  }: CustomOption) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      {flag && (
        <img
          src={flag}
          alt={`${country_name} flag`}
          style={{ width: '20px' }}
        />
      )}
      <span>{calling_code}</span>
    </div>
  );

  const handleCountryChange = (selectedOption: CustomOption | null) => {
    if (selectedOption) {
      setFormData((prev) => ({
        ...prev,
        country: selectedOption.value,
        countryCode: selectedOption.calling_code,
        mobile: '',
      }));
      setErrors((prev) => ({ ...prev, country: '' }));
    } else {
      setFormData((prev) => ({
        ...prev,
        country: '',
        countryCode: '',
      }));
    }
  };

  // Update countryCodeStyles for consistent height and border
  const countryCodeStyles = {
    control: (provided: any) => ({
      ...provided,
      borderRadius: '8px 0 0 8px',
      background: 'white',
      fontFamily: 'Latinka Regular',
      fontSize: '16px',
      color: '#090D28',
      minWidth: '100px',
      height: '44px',
      border: '2px solid #E4E9F0',
      borderRight: 'none',
      outline: 'none',
      boxShadow: 'none',
      display: 'flex',
      alignItems: 'center',
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#090D28',
      fontFamily: 'Latinka Regular',
      fontSize: '16px',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      display: 'none',
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      display: 'none',
    }),
    menuList: (provided: any) => ({
      ...provided,
      maxHeight: '300px',
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#195AFF',
        borderRadius: '3px',
      },
      '&::-webkit-scrollbar-track': {
        background: '#E6E9FF',
        borderRadius: '3px',
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: '#090D28',
      fontFamily: 'Latinka Regular',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      height: '100%',
    }),
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    let clean: string;
    if (/email/i.test(name)) {
      clean = value;
    } else if (name === 'mobile') {
      const maxLen = countries.find((c) => c.id.toString() === formData.country)?.phone_length || 10;
      clean = value.replace(/\D/g, '').slice(0, maxLen);
    } else {
      clean = stripDangerousChars(value);
    }
    setFormData((prev) => ({ ...prev, [name]: clean }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    setFormData((prev) => ({
      ...prev,
      'g-recaptcha-response': token || '',
    }));
    setErrors((prev) => ({ ...prev, captcha: '' }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.mobile.trim())
      newErrors.mobile = 'Mobile number is required';
    else if (
      countries.find((c) => c.id.toString() === formData.country)
        ?.phone_length &&
      formData.mobile.length !==
        countries.find((c) => c.id.toString() === formData.country)
          ?.phone_length
    ) {
      newErrors.mobile = `Mobile number must be ${countries.find((c) => c.id.toString() === formData.country)?.phone_length} digits`;
    }
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.description.trim())
      newErrors.description = 'Message is required';
    if (
      formData.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      newErrors.email = 'Invalid email format';
    }
    if (!recaptchaToken) newErrors.captcha = 'Please complete the reCAPTCHA';
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      if (validationErrors.captcha) {
        notifyerror(validationErrors.captcha);
      }
      return;
    }

    setFormLoading(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append('Content-Type', 'application/json');

      const raw = JSON.stringify({
        form_name: 'Blog Page form',
        data: {
          name: formData.name,
          mobile: formData.mobile,
          comment: formData.description,
          email: formData.email,
          countryCode: formData.countryCode,
          category: formData.category,
          city: formData.city,
          postcode: formData.postcode,
          company: formData.company,
          'g-recaptcha-response': formData['g-recaptcha-response'],
          source: 2,
        },
      });

      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: raw,
        redirect: 'follow' as RequestRedirect,
      };

      const response = await fetch(
        getApiUrl('/form-requests'),
        requestOptions,
      );
      const result = await response.json();
      if (response.status === 200) {
        notify();
        setFormData({
          category: '',
          name: '',
          email: '',
          country: formData.country,
          countryCode: formData.countryCode,
          mobile: '',
          city: '',
          postcode: '',
          company: '',
          description: '',
          'g-recaptcha-response': '',
        });
        setRecaptchaToken(null);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setErrors({});
      } else {
        notifyerror(
          result?.error || 'Form submission failed. Please try again.',
        );
      }
    } catch (error: any) {
      console.error(error);
      notifyerror(
        error.message || 'Form submission failed. Please try again.',
      );
    } finally {
      setFormLoading(false);
    }
  };

  const notify = () =>
    toast.success('Form submitted successfully.', {
      style: {
        border: '1px solid #00b852',
        padding: '16px',
        color: '#00b852',
        zIndex: '10000 !important',
      },
      iconTheme: {
        primary: '#00b852',
        secondary: '#FFFAEE',
      },
    });

  const notifyerror = (msg: string) =>
    toast.error(msg, {
      style: {
        border: '1px solid #cc0000',
        padding: '16px',
        color: '#cc0000',
        zIndex: '10000 !important',
      },
      iconTheme: {
        primary: '#cc0000',
        secondary: '#FFFAEE',
      },
    });

  // Helper to get category name by SEO url (categorySlug)
  const getCategoryName = (
    categoryData: string | number[] | null,
  ): string => {
    if (!categoryData) return 'Business';
    try {
      const parsedCategories =
        typeof categoryData === 'string'
          ? JSON.parse(categoryData)
          : categoryData;
      if (Array.isArray(parsedCategories) && parsedCategories.length > 0) {
        const categoryId = parseInt(String(parsedCategories[0]), 10);
        const category = categories.find((cat) => cat.id === categoryId);
        return category ? category.name : 'Business';
      }
      return 'Business';
    } catch (error) {
      console.error('Error parsing category data:', error);
      return 'Business';
    }
  };

  if (error) {
    return notFound();
  }

    if (!blog) {
    return notFound();
  }

  return (
    <div className='overflow-hidden'>
      <div className='mx-auto w-full px-4 md:container'>
        <div className='my-6 flex items-center gap-2 px-4 font-Schibsted_Grotesk text-sm font-normal leading-tight text-[#70708D]'>
          <Link href='/' className='font-Schibsted_Grotesk text-[#70708D]'>
            Home
          </Link>{' '}
          &gt;{' '}
          <Link
            className='font-Schibsted_Grotesk text-[#70708D]'
            href='/blog'
          >
            Blog
          </Link>{' '}
          &gt;{' '}
          {blog?.title?.length > 20
            ? blog?.title.slice(0, 20) + '...'
            : blog?.title}
        </div>

        <div className='block gap-[30px] md:block lg:flex'>
          <div className='w-full md:w-full lg:w-4/6'>
            <h1 className='mb-3 font-NeueMontreal text-[24px] font-[700] leading-[130%] text-[#090D28] md:text-[32px]'>
              {blog?.title}
            </h1>
            <p className='mb-[32px] font-Schibsted_Grotesk text-[14px] font-[400] leading-[120%] text-[#195AFF]'>
              {`${getCategoryName(blog?.categories)} | ${new Date(
                blog?.updated_at,
              ).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}`}
            </p>

            {loading ? (
              <div className='container mx-auto'>
                <div>
                  <div className='mb-3 h-3 animate-pulse rounded bg-gray-200'></div>
                  <div className='mb-3 h-3 w-1/2 animate-pulse rounded bg-gray-200'></div>
                  <div className='h-52 w-full animate-pulse rounded-t-xl bg-gray-200'></div>
                  <div className='flex animate-pulse space-x-4'>
                    <div className='flex-1 space-y-6 py-1'>
                      <div className='h-2 rounded bg-gray-200'></div>
                      <div className='space-y-3'>
                        <div className='grid grid-cols-3 gap-4'>
                          <div className='col-span-2 h-2 rounded bg-gray-200'></div>
                          <div className='col-span-1 h-2 rounded bg-gray-200'></div>
                        </div>
                        <div className='h-5 rounded bg-gray-200'></div>
                        <div className='h-2 rounded bg-gray-200'></div>
                        <div className='grid grid-cols-3 gap-4'>
                          <div className='col-span-2 h-2 rounded bg-gray-200'></div>
                          <div className='col-span-1 h-2 rounded bg-gray-200'></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className='mb-32 font-Schibsted_Grotesk'>
                {/* ✅ CHANGED: Use processContent to decode and preserve inline styles */}
                <div
                  className='blog-content'
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(processContent(blog?.content)),
                  }}
                />
              </div>
            )}

            <div className='mb-10 flex items-center gap-4'>
              <span className='font-Schibsted_Grotesk text-[14px] font-[600] leading-[100%] text-[#7E7E83]'>
                Share on
              </span>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}&title=${encodeURIComponent(
                  blog?.title || 'Blog Post',
                )}&summary=${shareText}&source=Vilva%20Business`}
                target='_blank'
                rel='noopener noreferrer'
              >
                <SvgIcons.FooterLinkedIn />
              </a>
              <a
                href={`https://www.instagram.com/?url=${shareUrl}`}
                target='_blank'
                rel='noopener noreferrer'
                onClick={() => {
                  toast(
                    'To share on Instagram, create a post with the link copied to your clipboard!',
                    {
                      style: {
                        border: '1px solid #195AFF',
                        padding: '16px',
                        color: '#195AFF',
                      },
                      iconTheme: {
                        primary: '#195AFF',
                        secondary: '#FFFAEE',
                      },
                    },
                  );
                  navigator.clipboard.writeText(
                    `${blog?.title || 'Blog Post'}: ${window.location.href}`,
                  );
                }}
              >
                <SvgIcons.FooterInsta />
              </a>
              <a
                href={`https://api.whatsapp.com/send?text=${shareText}%20${shareUrl}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                <SvgIcons.FooterWhatsapp />
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}&quote=${shareText}&picture=${shareImage}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                <SvgIcons.FooterFaceBook />
              </a>
              <div className='cursor-pointer' onClick={handleCopyLink}>
                {isLinkCopied ? (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='#195AFF'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <polyline points='20 6 9 17 4 12' />
                  </svg>
                ) : (
                  <SvgIcons.BlogLInkIcon />
                )}
              </div>
            </div>

            {blog && <CommentSectionView id={blog.id} />}
          </div>
          <div className='mt-[32px] w-full md:mt-0 md:w-full lg:w-2/6'>
            <section>
              <div className='mb-10 rounded-[12px] bg-[linear-gradient(136deg,_#01001E_2.93%,_#0132AE_100.67%)] px-3 py-8'>
                <h3 className='mb-4 text-center font-NeueMontreal text-[20px] font-[700] leading-[120%] tracking-wide text-white md:text-[24px]'>
                  Build Your Custom App/Website Book a Free Consultation
                  Today
                </h3>

                <form onSubmit={handleSubmit} onFocusCapture={() => setFormActivated(true)}>
                  <div className='mb-[16px]'>
                    <label
                      className='mb-1 block font-Schibsted_Grotesk text-[14px] font-normal leading-[100%] text-white'
                      htmlFor='name'
                    >
                      Full name *
                    </label>
                    <input
                      className={`w-full rounded-[8px] border-[2px] border-solid font-Schibsted_Grotesk ${
                        errors.name
                          ? 'border-red-500'
                          : 'border-[#E4E9F0]'
                      } h-[44px] bg-white px-[12px] text-[16px] font-normal leading-[120%] text-[#090D28] placeholder:font-Schibsted_Grotesk placeholder:text-[16px] placeholder:font-normal placeholder:text-[#090D28] focus:border-[#1D31C3] focus:bg-white focus:shadow-[0_0_4px_0_rgba(51,75,255,0.40)] focus:outline-none`}
                      type='text'
                      name='name'
                      id='name'
                      placeholder='Enter Name'
                      value={formData.name}
                      onChange={handleChange}
                    />
                    {errors.name && (
                      <p className='mt-1 text-[12px] text-red-500'>
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div className='mb-[16px]'>
                    <label
                      className='mb-1 block font-Schibsted_Grotesk text-[14px] font-normal leading-[100%] text-white'
                      htmlFor='mobile'
                    >
                      Mobile Number *
                    </label>
                    <div className='flex items-center'>
                      <div className='relative'>
                        <Select
                          name='country'
                          options={customOptions}
                          value={
                            customOptions.find(
                              (option) =>
                                option.value === formData.country,
                            ) || null
                          }
                          onChange={handleCountryChange}
                          formatOptionLabel={formatOptionLabel}
                          styles={countryCodeStyles}
                          placeholder='+00'
                          isDisabled={isCountryLoading}
                          isLoading={isCountryLoading}
                        />
                        <svg
                          className='absolute right-2 top-1/2 -translate-y-1/2 transform'
                          xmlns='http://www.w3.org/2000/svg'
                          width='20'
                          height='20'
                          viewBox='0 0 20 20'
                          fill='none'
                        >
                          <path
                            d='M5 7.5L10 12.5L15 7.5'
                            stroke='black'
                            strokeWidth='1.25'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </div>
                      <input
                        className={`w-full rounded-[8px] rounded-l-none border-[2px] border-solid font-Schibsted_Grotesk ${
                          errors.mobile || errors.country
                            ? 'border-red-500'
                            : 'border-[#E4E9F0]'
                        } h-[44px] bg-white px-[12px] text-[16px] font-normal leading-[120%] text-[#090D28] placeholder:font-Schibsted_Grotesk placeholder:text-[16px] placeholder:font-normal placeholder:text-[#090D28] focus:border-[#1D31C3] focus:bg-white focus:shadow-[0_0_4px_0_rgba(51,75,255,0.40)] focus:outline-none`}
                        type='text'
                        name='mobile'
                        id='mobile'
                        placeholder="Enter mobile number"
                        inputMode="numeric"
                        maxLength={countries.find((c) => c.id.toString() === formData.country)?.phone_length || 10}
                        value={formData.mobile}
                        onChange={handleChange}
                        disabled={isCountryLoading}
                      />
                    </div>
                    {(errors.mobile || errors.country) && (
                      <p className='mt-1 text-[12px] text-red-500'>
                        {errors.mobile || errors.country}
                      </p>
                    )}
                  </div>

                  <div className='mb-[16px]'>
                    <label
                      className='mb-1 block font-Schibsted_Grotesk text-[14px] font-normal leading-[100%] text-white'
                      htmlFor='email'
                    >
                      Work email (Optional)
                    </label>
                    <input
                      className={`w-full rounded-[8px] border-[2px] border-solid font-Schibsted_Grotesk ${
                        errors.email
                          ? 'border-red-500'
                          : 'border-[#E4E9F0]'
                      } h-[44px] bg-white px-[12px] text-[16px] font-normal leading-[120%] text-[#090D28] placeholder:font-Schibsted_Grotesk placeholder:text-[16px] placeholder:font-normal placeholder:text-[#090D28] focus:border-[#1D31C3] focus:bg-white focus:shadow-[0_0_4px_0_rgba(51,75,255,0.40)] focus:outline-none`}
                      type='email'
                      name='email'
                      id='email'
                      placeholder='Enter Email'
                      value={formData.email}
                      onChange={handleChange}
                    />
                    {errors.email && (
                      <p className='mt-1 font-Schibsted_Grotesk text-[12px] text-red-500'>
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div className='mb-[16px]'>
                    <label
                      className='mb-1 block font-Schibsted_Grotesk text-[14px] font-normal leading-[100%] text-white'
                      htmlFor='category'
                    >
                      Select Category *
                    </label>
                    <select
                      className={`w-full rounded-[8px] border-[2px] border-solid font-Schibsted_Grotesk ${
                        errors.category
                          ? 'border-red-500'
                          : 'border-[#E4E9F0]'
                      } h-[44px] bg-white px-[12px] text-[16px] font-normal leading-[120%] text-[#090D28] placeholder:font-Schibsted_Grotesk placeholder:text-[16px] placeholder:font-normal placeholder:text-[#090D28] focus:border-[#1D31C3] focus:bg-white focus:shadow-[0_0_4px_0_rgba(51,75,255,0.40)] focus:outline-none`}
                      name='category'
                      id='category'
                      value={formData.category}
                      onChange={handleChange}
                    >
                      <option
                        value=''
                        className='font-Schibsted_Grotesk'
                        disabled
                      >
                        Select Category
                      </option>
                      <option
                        className='font-Schibsted_Grotesk'
                        value='1'
                      >
                        Custom App/Website Development
                      </option>
                      <option
                        className='font-Schibsted_Grotesk'
                        value='2'
                      >
                        Web Design
                      </option>
                      <option
                        className='font-Schibsted_Grotesk'
                        value='3'
                      >
                        UI/UX Design
                      </option>
                      <option
                        className='font-Schibsted_Grotesk'
                        value='4'
                      >
                        Mobile App Development
                      </option>
                      <option
                        className='font-Schibsted_Grotesk'
                        value='5'
                      >
                        Web App Development
                      </option>
                      <option
                        className='font-Schibsted_Grotesk'
                        value='6'
                      >
                        Fintech App Development
                      </option>
                      <option
                        className='font-Schibsted_Grotesk'
                        value='7'
                      >
                        Digital Marketing
                      </option>
                      <option
                        className='font-Schibsted_Grotesk'
                        value='8'
                      >
                        Others
                      </option>
                    </select>
                    {errors.category && (
                      <p className='mt-1 font-Schibsted_Grotesk text-[12px] text-red-500'>
                        {errors.category}
                      </p>
                    )}
                  </div>

                  <div className='mb-[16px]'>
                    <label
                      className='mb-1 block font-Schibsted_Grotesk text-[14px] font-normal leading-[100%] text-white'
                      htmlFor='description'
                    >
                      Message *
                    </label>
                    <textarea
                      className={`w-full rounded-[8px] border-[2px] border-solid font-Schibsted_Grotesk ${
                        errors.description
                          ? 'border-red-500'
                          : 'border-[#E4E9F0]'
                      } h-[72px] bg-white px-[12px] pt-[10px] text-[16px] font-normal leading-[120%] text-[#090D28] placeholder:font-Schibsted_Grotesk placeholder:text-[16px] placeholder:font-normal placeholder:text-[#090D28] focus:border-[#1D31C3] focus:bg-white focus:shadow-[0_0_4px_0_rgba(51,75,255,0.40)] focus:outline-none`}
                      name='description'
                      id='description'
                      placeholder='Enter Message'
                      value={formData.description}
                      onChange={handleChange}
                    />
                    {errors.description && (
                      <p className='mt-1 font-Schibsted_Grotesk text-[12px] text-red-500'>
                        {errors.description}
                      </p>
                    )}
                  </div>

                  <div className='mb-[16px]'>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      className='flex w-full items-center justify-center font-Schibsted_Grotesk'
                      sitekey={recaptchaSiteKey}
                      onChange={handleRecaptchaChange}
                      onExpired={() => {
                        setRecaptchaToken(null);
                        setFormData((prev) => ({
                          ...prev,
                          'g-recaptcha-response': '',
                        }));
                        notifyerror(
                          'reCAPTCHA expired. Please try again.',
                        );
                      }}
                    />
                    {errors.captcha && (
                      <p className='mt-1 font-Schibsted_Grotesk text-[12px] text-red-500'>
                        {errors.captcha}
                      </p>
                    )}
                  </div>

                  <button
                    className={`flex h-[40px] w-full items-center justify-center rounded-[8px] font-Schibsted_Grotesk ${
                      !recaptchaToken || formLoading
                        ? 'cursor-not-allowed bg-gray-400'
                        : 'bg-[#195AFF]'
                    } px-5 py-4 font-Schibsted_Grotesk text-[16px] leading-[120%] text-white`}
                    type='submit'
                    disabled={!recaptchaToken || formLoading}
                  >
                    {formLoading
                      ? 'Submitting...'
                      : 'Get a Free Consultation'}
                  </button>
                </form>
              </div>
            </section>

            <section>
              <h2 className='mb-2 font-NeueMontreal text-[18px] font-[600] leading-[100%] text-[#090D28]'>
                About Vilva
              </h2>
              <div ref={aboutStatsRef} className='mb-6 flex flex-col items-start gap-6 self-stretch rounded-[8px] border-2 border-[#E2E8FB] bg-[#F6F8FE] p-6'>
                <p className='font-Schibsted_Grotesk text-[16px] font-[400] md:text-[18px]'>
                  Vilva Business is a B2B all-in-one service Portal with
                  solutions ranging from Marketing, Accounting, HR
                  Management, Business Printing Supplies, Technology
                  Services such as Website Development, Hosting, and
                  everything else a successful business needs for its
                  existence and growth.
                </p>
                <div className='flex w-full items-center justify-between'>
                  <div>
                    <p className='font-NeueMontreal text-[32px] font-[600] leading-[150%] text-[var(--Main-colour,_#195AFF)]'>
                      {Math.floor(aboutYears)}+
                    </p>
                    <p className='whitespace-nowrap font-Schibsted_Grotesk text-[18px] font-normal leading-[100%] text-[#090D28]'>
                      Years in business
                    </p>
                  </div>
                  <div>
                    <p className='font-NeueMontreal text-[32px] font-[600] leading-[150%] text-[var(--Main-colour,_#195AFF)]'>
                      {Math.floor(aboutCustomers)}+
                    </p>
                    <p className='whitespace-nowrap font-Schibsted_Grotesk text-[18px] font-normal leading-[100%] text-[#090D28]'>
                      Happy Customers
                    </p>
                  </div>
                </div>
                <div>
                  <div>
                    <p className='font-NeueMontreal text-[32px] font-[600] leading-[150%] text-[var(--Main-colour,_#195AFF)]'>
                      {Math.floor(aboutProjects)}+
                    </p>
                    <p className='whitespace-nowrap font-Schibsted_Grotesk text-[18px] font-normal leading-[100%] text-[#090D28]'>
                      Projects Delivered
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <BlogOurClients />

            <section className='mb-10'>
              <h2 className='mb-2 font-NeueMontreal text-[18px] font-[600] leading-[100%] text-[#090D28]'>
                Features & Ratings
              </h2>
              <div className='mb-6 flex flex-col items-start gap-6 self-stretch rounded-[8px] border-2 border-[#E2E8FB] bg-[#F6F8FE] p-6'>
                <div className='flex w-full justify-center'>
                  <a href='https://businessfirms.co/' target='_blank' aria-label="Business Firms website" rel="noopener noreferrer">
                    <Image
                      src={getImageUrl("/vilvabusiness/website/common/otherallimages/Frame%20642%20%282%29.webp")}
                      alt='Business Firms logo'
                      width={270}
                      height={140}
                    />
                  </a>
                </div>
                <div className='grid grid-cols-3 gap-6'>
                  
                    <Image src={getImageUrl("/vilvabusiness/website/blogs/blog_recent%20(1).webp")} alt='Features_img1'        width={270}
                      height={140} />
                  
                  
                    <Image src={getImageUrl("/vilvabusiness/website/blogs/blog_recent%20(2).webp")} alt='Features_img2'        width={270}
                      height={140} />
                  
                  <a
                    href='https://yourstory.com/tamil/tamil-t-shirts-spreading-tamil-language-entrepreneur'
                    target='_blank'
                  >
                    <Image src={getImageUrl("/vilvabusiness/website/blogs/blog_recent%20(3).webp")} alt='Features_img3'        width={270}
                      height={140} />
                  </a>
                  
                    <Image src={getImageUrl("/vilvabusiness/website/blogs/blog_recent%20(4).webp")} alt='Features_img4'        width={270}
                      height={140} />
                  
                  
                    <Image src={getImageUrl("/vilvabusiness/website/blogs/blog_recent%20(5).webp")} alt='Features_img5'        width={270}
                      height={140} />
                  
                </div>
              </div>
            </section>
          </div>
        </div>

        <h2 className='mb-6 font-NeueMontreal text-2xl font-bold'>
          Related Articles
        </h2>
        {blogs.length > 0 ? (
          <>
            <Swiper
              className='container mb-5'
              modules={[Navigation, Autoplay]}
              autoplay={{
                delay: 1000,
                disableOnInteraction: false,
              }}
              speed={3000}
              loop={true}
              spaceBetween={24}
              slidesPerView={1}
              navigation={{
                prevEl: '.seo-swiper-prev',
                nextEl: '.seo-swiper-next',
              }}
              breakpoints={{
                768: {
                  slidesPerView: 2,
                  spaceBetween: 24,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 24,
                },
              }}
            >
              {relatedBlogs
                .map((blogItem: any) => (
                  <SwiperSlide key={blogItem?.slug}>
                    <div className='h-[650px]'>
                      <Link
                        href={`/blog/${blogItem?.slug}`}
                        className='h-full'
                        rel='dofollow'
                      >
                        <div className='group relative h-full overflow-hidden rounded-[8px] bg-[#F6F8FE] transition-colors duration-300 ease-in-out hover:bg-gray-100'>
                          <div className='overflow-hidden rounded-t-[12px]'>
                            <img
                              src={blogItem?.image ? getImageUrl(`/${blogItem.image}`) : ''}
                              alt={blogItem?.title}
                              width={400}
                              height={300}
                              referrerPolicy="no-referrer"
                              className='h-[300px] w-full scale-100 transform object-cover transition-transform duration-300 ease-in-out group-hover:scale-105'
                            />
                          </div>
                          <div className='flex h-auto flex-col justify-between px-4 py-6 sm:h-[340px]'>
                            <div>
                              <span className='font-Schibsted_Grotesk text-[14px] font-[600] leading-[120%] text-[#195AFF]'>
                                {`${getCategoryName(blogItem?.categories)} | ${new Date(
                                  blogItem?.updated_at,
                                ).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                })}`}
                              </span>
                              <div className='mt-2'>
                                <h3 className='my-4 font-Schibsted_Grotesk text-[20px] font-[600] leading-[130%] text-[#090D28]'>
                                  {blogItem?.title}
                                </h3>
                                {/* ✅ CHANGED: Use processContent for short_content too */}
                                <div
                                  className='blog-content line-clamp-4 mb-4'
                                  dangerouslySetInnerHTML={{
                                    __html: sanitizeHtml(processContent(
                                      blogItem?.short_content,
                                    )),
                                  }}
                                />
                              </div>
                            </div>
                            <div>
                              <span
                                className='flex items-center gap-2 whitespace-nowrap font-Schibsted_Grotesk text-base font-[600] leading-none text-[#195AFF]'
                              >
                                <span className='w-full transition-all duration-300 ease-in-out group-hover:w-full group-hover:opacity-100 md:w-0 md:opacity-0'>
                                  Learn More
                                </span>
                                <svg
                                  xmlns='http://www.w3.org/2000/svg'
                                  width='20'
                                  height='20'
                                  viewBox='0 0 20 20'
                                  fill='none'
                                  className='block'
                                >
                                  <path
                                    d='M16.6667 10H3.33334'
                                    stroke='#195AFF'
                                    strokeWidth='1.5'
                                    strokeMiterlimit='10'
                                    strokeLinecap='round'
                                  />
                                
                                  <path
                                    d='M10.8063 16.3839L16.2837 10.9065C16.4033 10.7879 16.4983 10.6467 16.5631 10.491C16.628 10.3354 16.6615 10.1687 16.6615 10C16.6615 9.8314 16.628 9.6642 16.5631 9.5086C16.4983 9.353 16.4033 9.2118 16.2837 9.0923L10.8062 3.6144'
                                    stroke='#195AFF'
                                    strokeWidth='1.5'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                  />
                                </svg>
                              </span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </SwiperSlide>
                ))}
            </Swiper>
          </>
        ) : (
          <div className='mx-auto grid w-full animate-pulse gap-12 rounded-md p-4 md:grid-cols-2 lg:grid-cols-3'>
            <div>
              <div className='h-52 w-full rounded-t-xl bg-gray-200'></div>
              <div className='flex animate-pulse space-x-4'>
                <div className='flex-1 space-y-6 py-1'>
                  <div className='h-2 rounded bg-gray-200'></div>
                  <div className='space-y-3'>
                    <div className='grid grid-cols-3 gap-4'>
                      <div className='col-span-2 h-2 rounded bg-gray-200'></div>
                      <div className='col-span-1 h-2 rounded bg-gray-200'></div>
                    </div>
                    <div className='h-2 rounded bg-gray-200'></div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className='h-52 w-full rounded-t-xl bg-gray-200'></div>
              <div className='flex animate-pulse space-x-4'>
                <div className='flex-1 space-y-6 py-1'>
                  <div className='h-2 rounded bg-gray-200'></div>
                  <div className='space-y-3'>
                    <div className='grid grid-cols-3 gap-4'>
                      <div className='col-span-2 h-2 rounded bg-gray-200'></div>
                      <div className='col-span-1 h-2 rounded bg-gray-200'></div>
                      <div className='col-span-1 h-2 rounded bg-gray-200'></div>
                      <div className='col-span-2 h-2 rounded bg-gray-200'></div>
                    </div>
                    <div className='h-2 rounded bg-gray-200'></div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className='h-52 w-full rounded-t-xl bg-gray-200'></div>
              <div className='flex animate-pulse space-x-4'>
                <div className='flex-1 space-y-6 py-1'>
                  <div className='h-2 rounded bg-gray-200'></div>
                  <div className='space-y-3'>
                    <div className='grid grid-cols-3 gap-4'>
                      <div className='col-span-2 h-2 rounded bg-gray-200'></div>
                      <div className='col-span-1 h-2 rounded bg-gray-200'></div>
                    </div>
                    <div className='h-2 rounded bg-gray-200'></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogDetailView;